<template>
	<header-block v-if="customer" title="Notifications" bgcolor="white"></header-block>
	<CustomerLoadWait :customer="notifications" />
	<transition>
		<div v-if="notifications" class="notifications">
			<div class="notifications-list">
				<SwipeActionsContainer 
					:items="getDisplayedNotifications()"
					:backgroundColor="'var(--background-color-alt)'"
					:mailStyleSwipe="true"
					:actions="[
						{
							icon: 'mdi-delete',
							type: 'delete',
							color: 'white',
							backgroundColor: 'var(--background-color-alt2)'
						}
					]"
					@action-click="handleAction"
				>
					<template #default="{ item: n }">
						<v-row class="notificationRow" @click="navigateToPage(n)">
							<v-col cols="1" class="iconCol">
								<v-icon v-if="n.pendingMove" icon="mdi-bell" class="icon"></v-icon>
								<v-icon v-else icon="mdi-bell" class="icon opace"></v-icon>
							</v-col>
							<v-col>
								<h2 class="notification-title" :class="{ 'normal-weight': !n.pendingMove }">
									{{ n.title }}
									<img :src="require('@/assets/arrow-right.png')" class="rightIcon" />
								</h2>
								<div class="notification-body">
									<p :class="{ 'truncated': !expandedNotifications[n.id] }">{{ processMonetaryAmounts(n.body) }}</p>
									<a v-if="n.body.length > 100" @click="(e) => toggleExpand(n.id, e)" class="more-link">
										{{ expandedNotifications[n.id] ? 'Less' : 'More' }}
									</a>
								</div>
								<small>Sent {{ _csharpDateTimetoDateTime(n.sentDateTime) }}</small>
							</v-col>
						</v-row>
					</template>
				</SwipeActionsContainer>
			</div>

			<div v-if="hasOlderNotifications()" class="older-notifications-toggle">
				<a @click="showOlderNotifications = !showOlderNotifications" class="toggle-link">
					{{ showOlderNotifications ? 'Hide older notifications' : `Show ${getOlderNotificationsCount()} older notifications` }}
				</a>
			</div>

			<div class="ad">
				<h1>Stay in the loop</h1>
				<p>Make sure you have notifications enabled for the Money Guided app!</p>
				<small @click="$router.push('notifications')">Notification Settings</small>
			</div>
		</div>
	</transition>
	
</template>

<script>
import Services from '../mixins/Services';
import CustomerLoadWait from './elements/CustomerLoadWait.vue';
import HeaderBlock from './elements/HeaderBlock.vue';
import Helpers from '../mixins/Helpers';
import SwipeActionsContainer from './elements/SwipeActionsContainer.vue';

export default {
	mixins: [Services, Helpers],
	components: {CustomerLoadWait, HeaderBlock, SwipeActionsContainer},
	data() {
		return {
			notifications: null,
			expandedNotifications: {},
			showOlderNotifications: false,
			pendingMoves: [],
			daysToShow: 7
		}
	},
	computed: {
		// Remove all computed properties
	},
	methods: {
		getDisplayedNotifications() {
			if (!this.notifications) return [];
			
			const daysFrom = new Date();
			daysFrom.setDate(daysFrom.getDate() - this.daysToShow);
			
			var notificationsToShow = this.notifications.filter(n => {
				const notificationDate = new Date(n.sentDateTime);
				return this.showOlderNotifications || notificationDate >= daysFrom;
			});

			if (notificationsToShow.length === 0 && this.notifications.length > 0 && this.daysToShow == 7) {
				this.daysToShow = 30;
				this.getDisplayedNotifications();
			}

			return notificationsToShow;
		},
		hasOlderNotifications() {
			if (!this.notifications) return false;

			const daysFrom = new Date();
			daysFrom.setDate(daysFrom.getDate() - this.daysToShow);

			return this.notifications.some(n => new Date(n.sentDateTime) < daysFrom);
		},
		getOlderNotificationsCount() {
			if (!this.notifications) return 0;

			const daysFrom = new Date();
			daysFrom.setDate(daysFrom.getDate() - this.daysToShow);

			return this.notifications.filter(n => new Date(n.sentDateTime) < daysFrom).length;
		},
		onLoad: async function() {
			this._getCustomer();
			this._getNotifications(() => {
				this._getPendingMoves(() => {
					if (this.notifications && this.pendingMoves) {
						this.notifications = this.notifications.map(notification => {
							if (notification.appScreen && notification.appScreen.includes('Moves/')) {
								const moveGuid = notification.appScreen.split('Moves/')[1];
								const matchingMove = this.pendingMoves.find(m => m.move.id === moveGuid);
								if (matchingMove) {
									return {
										...notification,
										pendingMove: matchingMove
									};
								}
								else {
									notification.appScreen = 'noMove';
								}
							}
							return notification;
						});
					}
				}, false);
			});
		},
		handleAction({ item, action } = {}) {
			if (!item || !action) return;
			if (action.type === 'delete') {
				// Create a new array to ensure reactivity
				const updatedNotifications = this.notifications.filter(n => n.id !== item.id);
				this.notifications = updatedNotifications;
				
				// Clean up expanded state
				delete this.expandedNotifications[item.id];
				
				// Mark as deleted in the backend
				this._markAsDeleteNotification(item.id, () => {
					this._clearNotificationCache();
				});
			}
		},
		toggleExpand(id, event) {
			event.stopPropagation();
			this.expandedNotifications[id] = !this.expandedNotifications[id];
		},
		navigateToPage(notification) {
			// Stop event propagation to prevent triggering when clicking the more/less link
			event.stopPropagation();

			this._clearNotificationCache();
			
			// if there is a pendingMove, get its id and navigate to the move page
			if (notification.pendingMove) {
				this.$router.push('/moves/' + notification.pendingMove.id);
			} else {
				// Get the screen, removing "appscreen/Home/" if present, or default to AIOP
				let screen = notification.appScreen || 'Money';
				if (screen === 'noMove') {
					this.$router.push('/CompletedMoves');
				} else {
					this.$router.push('/' + screen);
				}
			}
		},
		processMonetaryAmounts(text) {
			if (localStorage.getItem('randomiseFigures') !== 'true') {
				return text;
			}

			// Match currency symbols (£, $) followed by numbers with optional decimal places
			// Also match numbers followed by currency words (pounds, pence, etc)
			const monetaryPattern = /(?:£|$)(?:\d{1,3}(?:,\d{3})*(?:\.\d{2})?|\d+(?:\.\d{2})?)|(?:\d+(?:\.\d{2})?\s*(?:pounds?|pence|p|GBP|USD))/gi;
			
			return text.replace(monetaryPattern, (match) => {
				// Replace the entire number portion with exactly 4 # characters
				// while preserving currency symbols and words
				if (match.startsWith('£') || match.startsWith('$')) {
					return match.charAt(0) + '####';
				} else {
					// For amounts with currency words, replace the number with ####
					return '####' + match.replace(/^\d+(?:\.\d{2})?/, '');
				}
			});
		}
	},
	mounted: function () {
		this.onLoad();
	},
}
</script>

<style scoped>
.notifications {
	margin-top: -20px;
	margin-left: -30px;
	margin-right: -30px;
}

.notifications-list {
	position: relative;
	z-index: 1;
}

.older-notifications-toggle {
	text-align: center;
	padding-top: 15px;
}

.toggle-link {
	color: var(--text-color);
	font-size: 12px;
	opacity: 0.7;
	cursor: pointer;
	text-decoration: none;
	display: inline-block;
	padding: 4px 0;
}

.toggle-link:hover {
	opacity: 1;
}

.notifications {
	margin-top: -20px;
	margin-left: -30px;
	margin-right: -30px;
	span {
		float: right;
	}
	h2 {
		font-size: 16px;
	}
	.meta{
		font-weight: bold;
	}
	.notificationRow{
		padding: 15px 30px;
		border-bottom: 1px solid var(--border-color);
	}

	.notificationRow:last-child {
		border-bottom: none;
	}
	
	.iconCol {
		padding-right: 30px;
		display: flex;
		align-items: center;
		justify-content: center;
	}
	
	.icon {
		width: 24px;
		border-radius: 50%;
		color: var(--background-color-alt2);
	}
	.opace {
		opacity: 0.5;
	}
	.ad{
		margin-top: 20px;
		background-color: var(--text-color);
		color: white;
		padding: 25px;
		h1{
			font-size: 20px;
		}
	}
}


.middleAlign {
	margin-top:auto;
	margin-bottom:auto;
}

.notification-body {
	position: relative;
}

.notification-body p {
	margin: 0;
	font-size: 14px;
	line-height: 1.4;
	overflow: hidden;
	transition: max-height 0.3s ease-in-out;
	max-height: 1000px; /* Default to expanded */
}

.notification-body p.truncated {
	max-height: 40px;
}

.notification-body p:not(.truncated) {
	max-height: 1000px;
}

.more-link {
	color: var(--text-color);
	text-decoration: underline;
	font-size: 12px;
	cursor: pointer;
	display: inline-block;
	margin-top: 4px;
}

.notification-title {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin: 0;
	font-size: 16px;
	font-weight: bold;
}

.normal-weight {
	font-weight: normal;
}

.rightIcon {
	height: 10px;
	margin-top:auto;
	margin-bottom:auto;
	opacity: 0.5;
}

</style>
